import { forwardRef } from 'react';
import { Icon } from './Icon';
import { IconButton, type IconButtonProps } from './Button';
import { note } from '../theme/tokens/colors';

export type ComposeSendTone = 'brand' | 'note';

export interface ComposeSendButtonProps
	extends Omit<IconButtonProps, 'icon' | 'variant' | 'children' | 'aria-label'> {
	/** Accessible name — required; defaults are fine for most composers. */
	'aria-label'?: string;
	/**
	 * `brand` — primary message send (Apple Blue).
	 * `note` — private note-to-self (sticky-note ink).
	 */
	tone?: ComposeSendTone;
}

/**
 * Circular arrow-up send control for message composers.
 *
 * Sit it in a `position="relative"` wrap around the textarea; it defaults to
 * absolute bottom/right so every chat UI shares the same shape and placement.
 * Color comes from tokens — never pass raw hex at the call site.
 */
export const ComposeSendButton = forwardRef<HTMLButtonElement, ComposeSendButtonProps>(
	(
		{
			'aria-label': ariaLabel = 'Send',
			tone = 'brand',
			size = 'sm',
			minW = '28px',
			h = '28px',
			position = 'absolute',
			bottom = '8px',
			right = '8px',
			zIndex = 2,
			...rest
		},
		ref,
	) => {
		const isNote = tone === 'note';
		return (
			<IconButton
				ref={ref}
				aria-label={ariaLabel}
				icon={<Icon name="arrowUp" size="md" />}
				size={size}
				variant="solid"
				colorScheme={isNote ? undefined : 'brand'}
				borderRadius="full"
				minW={minW}
				h={h}
				position={position}
				bottom={bottom}
				right={right}
				zIndex={zIndex}
				{...(isNote
					? {
							bg: note.ink,
							color: 'fg.onBrand',
							_hover: { bg: note.inkHover },
							_disabled: {
								bg: note.ink,
								opacity: 0.6,
								cursor: 'not-allowed',
							},
						}
					: {
							_disabled: {
								opacity: 0.6,
								cursor: 'not-allowed',
							},
						})}
				{...rest}
			/>
		);
	},
);
ComposeSendButton.displayName = 'ComposeSendButton';

export default ComposeSendButton;
